import { useState, useEffect, useCallback } from 'react';
import { Target, Plus, Clock, CheckCircle, X, AlertCircle, Trophy, ChevronDown, ChevronUp, MessageSquare, Eye, Sparkles } from '../icons';
import {
  fetchOutcomes, acceptOutcome, rejectOutcome, updateOutcome,
  addOutcomeMilestone, toggleOutcomeMilestone, deleteOutcomeMilestone,
  toggleGoalSharedWithCoach,
  type NlpOutcome,
} from '../../lib/progress';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../LoadingSpinner';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-cream-200 text-chocolate-600' },
  proposed: { label: 'Proposed', color: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Accepted', color: 'bg-sage-100 text-sage-700' },
  active: { label: 'Active', color: 'bg-sage-100 text-sage-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-600' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700' },
  paused: { label: 'Paused', color: 'bg-cream-200 text-chocolate-500' },
};

export function OutcomesView() {
  const { user } = useAuth();
  const [outcomes, setOutcomes] = useState<NlpOutcome[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedOutcome, setExpandedOutcome] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [showFeedbackFor, setShowFeedbackFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    const data = await fetchOutcomes(user.id);
    setOutcomes(data);
    setIsLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async (id: string) => {
    await acceptOutcome(id, feedbackText || undefined);
    setFeedbackText('');
    setShowFeedbackFor(null);
    await load();
  };

  const handleReject = async (id: string) => {
    await rejectOutcome(id, feedbackText || undefined);
    setFeedbackText('');
    setShowFeedbackFor(null);
    await load();
  };

  const handleAddMilestone = async (outcomeId: string) => {
    if (!user) return;
    const title = prompt('Milestone title:');
    if (!title) return;
    await addOutcomeMilestone(outcomeId, user.id, title);
    await load();
  };

  const handleToggleMilestone = async (milestoneId: string, achieved: boolean) => {
    await toggleOutcomeMilestone(milestoneId, achieved);
    await load();
  };

  const handleDeleteMilestone = async (id: string) => {
    await deleteOutcomeMilestone(id);
    await load();
  };

  if (isLoading) return <LoadingSpinner message="Loading outcomes..." />;

  const proposed = outcomes.filter(o => o.status === 'proposed');
  const active = outcomes.filter(o => o.status === 'active' || o.status === 'accepted');
  const other = outcomes.filter(o => !['proposed', 'active', 'accepted'].includes(o.status));

  return (
    <div className="p-6 md:p-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
          <Target className="w-5 h-5 text-sage-600" />
        </div>
        <div>
          <h1 className="freud-heading-xl text-chocolate-900">Collaborative Outcomes</h1>
          <p className="text-sm text-chocolate-500">
            {proposed.length > 0 ? `${proposed.length} awaiting your review` : 'NLP Well-Formed Outcomes from your coach'}
          </p>
        </div>
      </div>

      {/* Proposed outcomes — need client review */}
      {proposed.length > 0 && (
        <div className="mb-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            Awaiting your review
          </h2>
          <div className="space-y-3">
            {proposed.map(outcome => (
              <div key={outcome.id} className="freud-card-elevated p-5 border-l-4 border-amber-400">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Proposed by coach</span>
                  <span className="text-[11px] text-chocolate-400 capitalize">{outcome.goal_type}</span>
                </div>
                <p className="text-sm font-medium text-chocolate-900 mb-2">
                  {outcome.outcome_statement || outcome.desired_outcome || 'Untitled outcome'}
                </p>
                {outcome.purpose && <p className="text-xs text-chocolate-500 mb-2">Why: {outcome.purpose}</p>}
                {outcome.timeframe && (
                  <p className="text-xs text-chocolate-400 mb-3">Target: {new Date(outcome.timeframe).toLocaleDateString()}</p>
                )}

                <div className="flex items-center gap-2 mb-3">
                  <button
                    onClick={() => handleAccept(outcome.id)}
                    className="freud-btn-primary text-sm flex items-center gap-1.5"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Accept
                  </button>
                  <button
                    onClick={() => handleReject(outcome.id)}
                    className="px-3 py-2 rounded-lg text-sm font-medium border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors flex items-center gap-1.5"
                  >
                    <X className="w-4 h-4" />
                    Request changes
                  </button>
                  <button
                    onClick={() => setShowFeedbackFor(showFeedbackFor === outcome.id ? null : outcome.id)}
                    className="text-xs text-chocolate-500 hover:text-chocolate-700 underline ml-2"
                  >
                    Add feedback
                  </button>
                </div>

                {showFeedbackFor === outcome.id && (
                  <div className="mb-3">
                    <textarea
                      className="freud-input text-sm"
                      rows={2}
                      placeholder="Optional: share your thoughts with your coach..."
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                    />
                  </div>
                )}

                <button
                  onClick={() => setExpandedOutcome(expandedOutcome === outcome.id ? null : outcome.id)}
                  className="text-xs text-chocolate-500 hover:text-chocolate-700 flex items-center gap-1"
                >
                  {expandedOutcome === outcome.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  View full outcome
                </button>

                {expandedOutcome === outcome.id && (
                  <div className="mt-4 pt-4 border-t border-cream-200 grid grid-cols-2 gap-3">
                    {[
                      ['Desired outcome', outcome.desired_outcome],
                      ['Positive direction', outcome.positive_direction],
                      ['Purpose', outcome.purpose],
                      ['Evidence', outcome.evidence],
                      ['Baseline', outcome.measurement_baseline],
                      ['Target', outcome.measurement_target],
                      ['Context', outcome.context],
                      ['Personal control', outcome.personal_control],
                      ['Resources', outcome.resources],
                      ['Ecology', outcome.ecology],
                      ['Obstacles', outcome.obstacles],
                      ['Milestones', outcome.milestones_text],
                      ['First action', outcome.first_action],
                    ].map(([label, val]) => val ? (
                      <div key={label as string}>
                        <p className="text-[11px] font-medium text-chocolate-400">{label as string}</p>
                        <p className="text-xs text-chocolate-700">{val as string}</p>
                      </div>
                    ) : null)}
                    {outcome.commitment_level && (
                      <div className="flex gap-4 text-xs">
                        <span className="text-chocolate-500">Commitment: <strong className="text-chocolate-800">{outcome.commitment_level}/10</strong></span>
                        <span className="text-chocolate-500">Confidence: <strong className="text-chocolate-800">{outcome.confidence_level}/10</strong></span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active outcomes */}
      {active.length > 0 && (
        <div className="mb-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-sage-600" />
            Active outcomes
          </h2>
          <div className="space-y-3">
            {active.map(outcome => {
              const milestones = outcome.milestones || [];
              const achievedCount = milestones.filter(m => m.achieved).length;
              const pct = milestones.length > 0 ? Math.round((achievedCount / milestones.length) * 100) : 0;
              const nextMilestone = milestones.find(m => !m.achieved);
              const isExpanded = expandedOutcome === outcome.id;

              return (
                <div key={outcome.id} className="freud-card-elevated p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sage-100 text-sage-700">Active</span>
                        <span className="text-[11px] text-chocolate-400 capitalize">{outcome.goal_type}</span>
                        {outcome.timeframe && (
                          <span className="text-[11px] text-chocolate-400">by {new Date(outcome.timeframe).toLocaleDateString()}</span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-chocolate-900">
                        {outcome.outcome_statement || outcome.desired_outcome}
                      </p>
                      {outcome.purpose && <p className="text-xs text-chocolate-500 mt-0.5">Why: {outcome.purpose}</p>}
                    </div>
                    <button
                      onClick={() => setExpandedOutcome(isExpanded ? null : outcome.id)}
                      className="p-1.5 rounded-lg text-chocolate-400 hover:bg-cream-100"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Progress */}
                  {milestones.length > 0 && (
                    <div className="mt-3">
                      <div className="flex justify-between mb-1">
                        <span className="text-[11px] text-chocolate-500">{achievedCount}/{milestones.length} milestones</span>
                        <span className="text-[11px] font-bold text-chocolate-700">{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden">
                        <div className="h-full bg-sage-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Quick view: next milestone + next action + coach feedback */}
                  <div className="mt-3 space-y-1.5">
                    {nextMilestone && (
                      <p className="text-xs text-chocolate-600">
                        <strong>Next milestone:</strong> {nextMilestone.title}
                      </p>
                    )}
                    {outcome.first_action && (
                      <p className="text-xs text-chocolate-600">
                        <strong>Next action:</strong> {outcome.first_action}
                      </p>
                    )}
                    {outcome.coach_feedback && (
                      <div className="flex items-start gap-1.5 mt-2">
                        <MessageSquare className="w-3.5 h-3.5 text-sage-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-chocolate-600 italic">"{outcome.coach_feedback}"</p>
                      </div>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-cream-200 space-y-3">
                      {/* Milestones */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-chocolate-600">Milestones</p>
                          <button onClick={() => handleAddMilestone(outcome.id)} className="text-xs text-sage-600 hover:text-sage-700 font-medium">
                            + Add
                          </button>
                        </div>
                        {milestones.map(m => (
                          <div key={m.id} className="flex items-center gap-2 py-1">
                            <button
                              onClick={() => handleToggleMilestone(m.id, !m.achieved)}
                              className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                                m.achieved ? 'bg-sage-500 border-sage-500' : 'border-cream-300 hover:border-sage-400'
                              }`}
                            >
                              {m.achieved && <CheckCircle className="w-3 h-3 text-white" />}
                            </button>
                            <span className={`text-xs flex-1 ${m.achieved ? 'text-chocolate-400 line-through' : 'text-chocolate-700'}`}>
                              {m.title}
                            </span>
                            {m.target_date && <span className="text-[10px] text-chocolate-400">{new Date(m.target_date).toLocaleDateString()}</span>}
                            <button onClick={() => handleDeleteMilestone(m.id)} className="text-chocolate-300 hover:text-red-500">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Full NLP fields */}
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          ['Desired outcome', outcome.desired_outcome],
                          ['Positive direction', outcome.positive_direction],
                          ['Purpose', outcome.purpose],
                          ['Evidence', outcome.evidence],
                          ['Baseline', outcome.measurement_baseline],
                          ['Target', outcome.measurement_target],
                          ['Context', outcome.context],
                          ['Personal control', outcome.personal_control],
                          ['Resources', outcome.resources],
                          ['Ecology', outcome.ecology],
                          ['Obstacles', outcome.obstacles],
                          ['Milestones', outcome.milestones_text],
                          ['First action', outcome.first_action],
                        ].map(([label, val]) => val ? (
                          <div key={label as string}>
                            <p className="text-[11px] font-medium text-chocolate-400">{label as string}</p>
                            <p className="text-xs text-chocolate-700">{val as string}</p>
                          </div>
                        ) : null)}
                      </div>

                      {outcome.commitment_level && (
                        <div className="flex gap-4 text-xs">
                          <span className="text-chocolate-500">Commitment: <strong className="text-chocolate-800">{outcome.commitment_level}/10</strong></span>
                          <span className="text-chocolate-500">Confidence: <strong className="text-chocolate-800">{outcome.confidence_level}/10</strong></span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Other outcomes (completed, rejected, paused) */}
      {other.length > 0 && (
        <div>
          <h2 className="freud-heading-lg text-chocolate-900 mb-3 text-chocolate-500">History</h2>
          <div className="space-y-2">
            {other.map(outcome => {
              const config = STATUS_CONFIG[outcome.status] || STATUS_CONFIG.draft;
              return (
                <div key={outcome.id} className="freud-card-elevated p-3 flex items-center gap-3">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${config.color}`}>{config.label}</span>
                  <span className="text-sm text-chocolate-600 truncate flex-1">
                    {outcome.outcome_statement || outcome.desired_outcome}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {outcomes.length === 0 && (
        <div className="freud-card-elevated p-12 text-center">
          <Target className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-sm text-chocolate-500 mb-1">No collaborative outcomes yet.</p>
          <p className="text-xs text-chocolate-400">When your coach creates an NLP outcome for you, it will appear here for your review.</p>
        </div>
      )}
    </div>
  );
}
